import React, { useState } from 'react';
import { array, bool, func, shape, string, number, object } from 'prop-types';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/UI.duck';
import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import { useConfirmationModal } from '../../util/hooks';
import { parse } from '../../util/urlHelpers';
import { ensureCurrentUser, ensureListing } from '../../util/data';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { findOptionsForSelectFilter } from '../../util/search';
import { prepareSocialSharingImages } from '../../util/seo';
import { addComment, deleteComment, editComment, closeListing, addPostCount, voteComment, translateContent } from './PostPage.duck';
import {
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  ShareModal,
  NamedLink,
} from '../../components';
import { TopbarContainer } from '..';
import routeConfiguration from '../../routeConfiguration';
import config from '../../config';
import Post from './Post/Post';
import Sidebar from '../CommunityPage/Sidebar/Sidebar';
import { PrevArrow } from '../../components/Slider/Arrows';
import postPageSharingImage from '../../assets/attractrCommunity-1200x630.avif';
import css from './PostPage.module.css';

/**
 * Helper Functions
 */

/**
 * Extract search state from location for maintaining filter state between pages
 */
const getSearchState = location => {
  const locationState = location?.state;
  return locationState?.search && locationState.search !== ''
    ? { to: { search: `?${locationState.search.replace('?', '')}` } }
    : {};
};

/**
 * Get post from Redux state by id
 */
const getPostFromState = (state, postId) => {
  const ref = { id: postId, type: 'listing' };
  const posts = getMarketplaceEntities(state, [ref]);
  return posts.length === 1 ? posts[0] : null;
};

/**
 * Get formatted schema title for SEO
 */
const getSchemaTitle = (intl, siteTitle, postTitle) => {
  return intl.formatMessage({ id: 'PostPage.schemaTitle' }, { siteTitle, postTitle });
};

/**
 * Handle adding a post count (votes, views, etc.)
 * Redirects to login if user is not authenticated
 */
const handleAddPostCount = ({
  onAddPostCount,
  currentPost,
  currentUser,
  history,
  location,
  action,
}) => {
  if (currentUser) {
    onAddPostCount({
      listingId: currentPost.id.uuid,
      action,
    });
  } else {
    const routes = routeConfiguration();
    const state = {
      from: `${location.pathname}${location.search}${location.hash}`,
    };
    return history.push(createResourceLocatorString('LoginPage', routes), state);
  }
};

/**
 * Handle adding a comment to a post
 * @returns {Promise} - Returns the promise from onAddComment for chaining
 */
const handleAddComment = (onAddComment, currentUser, currentPost, values) => {
  return onAddComment({
    userId: currentUser.id.uuid,
    postId: currentPost.id.uuid,
    parentId: values.parentId,
    content: values.content,
  });
};

/**
 * Handle deleting a comment from a post
 * @param {Function} onDeleteComment - Delete comment action
 * @param {Object} currentPost - Current post
 * @param {String} commentId - ID of comment to delete
 * @param {Boolean} hasReplies - Whether the comment has replies
 */
const handleDeleteComment = (onDeleteComment, currentPost, commentId, hasReplies) => {
  onDeleteComment({
    commentId,
    postId: currentPost?.id?.uuid,
    hasReplies,
  });
};

/**
 * Handle editing a comment
 * @param {Function} onEditComment - Edit comment action
 * @param {Object} currentPost - Current post
 * @param {String} commentId - ID of comment to edit
 * @param {String} updatedContent - New content for the comment
 */
const handleEditComment = (onEditComment, currentPost, commentId, updatedContent) => {
  return onEditComment(commentId, updatedContent, currentPost?.id?.uuid);
};

/**
 * Handle closing (removing) a post with confirmation
 */
const handleCloseListing = async (
  onOpenConfirmationModalWithHandlers,
  onUpdateConfirmationModalProps,
  onCloseConfirmationModal,
  onCloseListing,
  currentPost,
  history,
  intl,
  setRemovePostInProgress
) => {
  onOpenConfirmationModalWithHandlers({
    id: 'PostPage.confirmationModal',
    title: intl.formatMessage({ id: 'PostPage.deletePostConfirmationModalTitle' }),
    description: intl.formatMessage({ id: 'PostPage.deletePostConfirmationModalDescription' }),
    onConfirm: async () => {
      try {
        // Set loading state
        setRemovePostInProgress(true);
        onUpdateConfirmationModalProps({ inProgress: true });

        // Perform the deletion
        await onCloseListing(currentPost.id);

        // Wait a bit for user feedback, then close modal and navigate
        setTimeout(() => {
          onCloseConfirmationModal();
          setRemovePostInProgress(false);
          history.push(pathByRouteName('CommunityPage', routeConfiguration()));
        }, 1000);
      } catch (error) {
        // Handle error case
        onCloseConfirmationModal();
        setRemovePostInProgress(false);
        // You might want to show an error message here
        console.error('Failed to delete post:', error);
      }
    },
    onCancel: () => {
      // User cancelled, just close modal
      onCloseConfirmationModal();
      setRemovePostInProgress(false);
    },
  });
};

/**
 * Main PostPage component - displays a single post with comments and sidebar
 */
const PostPageComponent = props => {
  // State
  const [isShareModalOpen, setShareModalOpen] = useState(false);
  const [isRemovePostInProgress, setRemovePostInProgress] = useState(false);
  const {
    openWithHandlers: onOpenConfirmationModalWithHandlers,
    updateProps: onUpdateConfirmationModalProps,
    closeModal: onCloseConfirmationModal,
    modalElement,
  } = useConfirmationModal();

  // Props
  const {
    state,
    currentUser,
    scrollingDisabled,
    onManageDisableScrolling,
    intl,
    history,
    location,
    filterConfig,
    post,
    showPostInProgress,
    showPostError,
    linkPreview,
    onAddComment,
    addCommentInProgress,
    addCommentError,
    onDeleteComment,
    currentDeleteCommentId,
    deletedCommentIds,
    deleteCommentInProgress,
    deleteCommentError,
    onEditComment,
    editCommentInProgress,
    editCommentError,
    onCloseListing,
    addPostCountInProgress,
    addPostCountError,
    onAddPostCount,
    voteCount,
    currentUserVote,
    onVoteComment,
    onTranslate,
    sidebarTeam,
    sidebarMemberCount,
  } = props;

  // Derived Values
  const { siteTitle } = config;
  const categories = findOptionsForSelectFilter('community-categories', filterConfig);
  const disciplines = findOptionsForSelectFilter('community-disciplines', filterConfig);
  const roles = findOptionsForSelectFilter('community-roles', filterConfig);
  const topics = findOptionsForSelectFilter('community-topics', filterConfig);
  const currentSearchParams = parse(location.search);
  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const currentPost = ensureListing(post);
  const postTitle = currentPost.attributes.title;
  const searchMaybe = getSearchState(location);
  const schemaTitle = getSchemaTitle(intl, siteTitle, postTitle);

  // Handlers
  const handleOpenShareModal = () => setShareModalOpen(true);
  const handleCloseShareModal = () => setShareModalOpen(false);

  const handleAddCommentWithParams = values => {
    return handleAddComment(onAddComment, ensuredCurrentUser, currentPost, values);
  };

  const handleDeleteCommentWithParams = (commentId, hasReplies) => {
    return handleDeleteComment(onDeleteComment, currentPost, commentId, hasReplies);
  };

  const handleEditCommentWithParams = (commentId, updatedContent) => {
    return handleEditComment(onEditComment, currentPost, commentId, updatedContent);
  };

  const handleAddPostCountWithParams = action => {
    handleAddPostCount({
      onAddPostCount,
      currentPost,
      currentUser,
      history,
      location,
      action,
    });
  };

  const handleCloseListingWithParams = () => {
    handleCloseListing(
      onOpenConfirmationModalWithHandlers,
      onUpdateConfirmationModalProps,
      onCloseConfirmationModal,
      onCloseListing,
      currentPost,
      history,
      intl,
      setRemovePostInProgress
    );
  };

  // Render Elements
  const returnLink = (
    <NamedLink className={css.returnLink} name="CommunityPage" {...searchMaybe}>
      <PrevArrow />
    </NamedLink>
  );

  return (
    <Page
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'PostPage',
        name: schemaTitle,
      }}
      socialSharing={{
        images1200: prepareSocialSharingImages([postPageSharingImage]),
      }}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="PostPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <div className={css.page}>
            {!showPostError && returnLink}
            <div className={css.pageWrapper}>
              <Post
                state={state}
                intl={intl}
                currentPost={currentPost}
                linkPreview={linkPreview}
                categories={categories}
                location={location}
                currentUser={ensuredCurrentUser}
                showPostInProgress={showPostInProgress}
                showPostError={showPostError}
                addCommentInProgress={addCommentInProgress}
                addCommentError={addCommentError}
                deleteCommentInProgress={deleteCommentInProgress}
                deleteCommentError={deleteCommentError}
                deletedCommentIds={deletedCommentIds}
                currentDeleteCommentId={currentDeleteCommentId}
                editCommentInProgress={editCommentInProgress}
                editCommentError={editCommentError}
                isRemovePostInProgress={isRemovePostInProgress}
                addPostCountInProgress={addPostCountInProgress}
                addPostCountError={addPostCountError}
                voteCount={voteCount}
                currentUserVote={currentUserVote}
                commentsFromState={props.commentsFromState}
                onAddPostCount={handleAddPostCountWithParams}
                onOpenShareModal={handleOpenShareModal}
                onAddComment={handleAddCommentWithParams}
                onDeleteComment={handleDeleteCommentWithParams}
                onEditComment={handleEditCommentWithParams}
                onVoteComment={onVoteComment}
                onTranslate={onTranslate}
                onCloseListing={handleCloseListingWithParams}
                onManageDisableScrolling={onManageDisableScrolling}
                onOpenConfirmationModalWithHandlers={onOpenConfirmationModalWithHandlers}
                onUpdateConfirmationModalProps={onUpdateConfirmationModalProps}
                onCloseConfirmationModal={onCloseConfirmationModal}
              />
              <Sidebar
                className={css.sidebar}
                categories={categories}
                disciplines={disciplines}
                roles={roles}
                topics={topics}
                currentSearchParams={currentSearchParams}
                team={sidebarTeam}
                memberCount={sidebarMemberCount}
                onManageDisableScrolling={onManageDisableScrolling}
              />
            </div>
            <ShareModal
              id="PostPage.shareModal"
              location={location}
              isOpen={isShareModalOpen}
              onCloseModal={handleCloseShareModal}
              onManageDisableScrolling={onManageDisableScrolling}
              titleMessage={intl.formatMessage({ id: 'PostPage.shareModalTitle' })}
            />
            {modalElement}
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer className={css.footer} />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * Prop Types and Default Props
 */
PostPageComponent.defaultProps = {
  filterConfig: config.custom.filters,
  showPostInProgress: false,
  showPostError: null,
  linkPreview: null,
  addCommentInProgress: false,
  addCommentError: null,
  deleteCommentInProgress: false,
  deleteCommentError: null,
  currentDeleteCommentId: null,
  deletedCommentIds: [],
  editCommentInProgress: false,
  editCommentError: null,
  addPostCountInProgress: false,
  addPostCountError: null,
  voteCount: 0,
  currentUserVote: null,
  commentsFromState: [],
};

PostPageComponent.propTypes = {
  // Required props
  state: object.isRequired,
  currentUser: propTypes.currentUser,
  scrollingDisabled: bool.isRequired,
  onManageDisableScrolling: func.isRequired,
  intl: intlShape.isRequired,
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,
  post: propTypes.listing,
  onAddComment: func.isRequired,
  onDeleteComment: func.isRequired,
  onEditComment: func.isRequired,
  onAddPostCount: func.isRequired,
  onCloseListing: func.isRequired,

  // Optional props
  filterConfig: propTypes.filterConfig,
  showPostInProgress: bool,
  showPostError: propTypes.error,
  linkPreview: shape({
    id: propTypes.uuid,
    linkPreview: shape({
      title: string,
      description: string,
      image: string,
      url: string,
    }),
  }),
  addCommentInProgress: bool,
  addCommentError: propTypes.error,
  deleteCommentInProgress: bool,
  deleteCommentError: propTypes.error,
  currentDeleteCommentId: string,
  deletedCommentIds: array,
  editCommentInProgress: bool,
  editCommentError: propTypes.error,
  addPostCountInProgress: bool,
  addPostCountError: propTypes.error,
  voteCount: number,
  currentUserVote: string,
  commentsFromState: array,
};

/**
 * Redux Connection
 */
const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    postId,
    showPostInProgress,
    showPostError,
    linkPreview,
    addCommentInProgress,
    addCommentError,
    currentDeleteCommentId,
    deletedCommentIds,
    deleteCommentInProgress,
    deleteCommentError,
    editCommentInProgress,
    editCommentError,
    addPostCountInProgress,
    addPostCountError,
    voteCount,
    currentUserVote,
    currentListing,
    sidebarTeam,
    sidebarMemberCount,
  } = state.PostPage;

  // Get comments from Redux state if available for real-time updates
  const commentsFromState = currentListing?.attributes?.publicData?.comments || [];

  return {
    state,
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    post: getPostFromState(state, postId),
    showPostInProgress,
    showPostError,
    linkPreview,
    addCommentInProgress,
    addCommentError,
    deleteCommentInProgress,
    deleteCommentError,
    currentDeleteCommentId,
    deletedCommentIds,
    editCommentInProgress,
    editCommentError,
    addPostCountInProgress,
    addPostCountError,
    voteCount,
    currentUserVote,
    commentsFromState,
    sidebarTeam,
    sidebarMemberCount,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onAddComment: params => dispatch(addComment(params)),
  onDeleteComment: params => dispatch(deleteComment(params)),
  onEditComment: (commentId, updatedContent, listingId) => dispatch(editComment(commentId, updatedContent, listingId)),
  onCloseListing: listingId => dispatch(closeListing(listingId)),
  onAddPostCount: params => dispatch(addPostCount(params)),
  onVoteComment: (commentId, action, listingId) => dispatch(voteComment(commentId, action, listingId)),
  onTranslate: params => dispatch(translateContent(params)),
});

const PostPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withRouter,
  injectIntl
)(PostPageComponent);

export default PostPage;
